import type { Txn } from '../transactions/transactionWire'

/** A bounded number of `/match` requests per page: the largest groups are where a suggestion saves the most. */
export const BRAND_GROUPS_MAX = 3

/** Rows of the No merchant page that share a cleaned merchant name: one `/match` each. Groups that match one brand share its one suggestion (`useBrandGroups`). */
export type NameGroup = { name: string; rows: Txn[] }

/**
 * The No merchant page's brand suggestions (#494) are asked once per row group, not once per row: rows are
 * grouped by their cleaned merchant name (`Txn.merchant`, compared trimmed and case-folded; the first
 * spelling names the group). Only rows a merchant can be set on count, as for "Set all N at once?":
 * no merchant yet, not pending. Largest first, ties in list order so suggestions do not reorder as the
 * page re-renders; at most `BRAND_GROUPS_MAX`.
 */
export function nameGroups(rows: readonly Txn[]): NameGroup[] {
  const groups = new Map<string, NameGroup>()
  for (const r of rows) {
    if (r.merchantId !== null || r.isPending) continue
    const name = r.merchant.trim()
    if (name === '') continue
    const key = name.toLocaleLowerCase()
    const g = groups.get(key)
    if (g === undefined) groups.set(key, { name, rows: [r] })
    else g.rows.push(r)
  }
  // Array.prototype.sort is stable: equal sizes keep insertion (list) order.
  return [...groups.values()].sort((a, b) => b.rows.length - a.rows.length).slice(0, BRAND_GROUPS_MAX)
}
